import {
	type CreditUsageStatusType,
	JobFunctionNameSchema,
	type Job,
	JobSchema,
	JobStatusSchema,
	type JobStatusType,
	type JobUncheckedCreateInputSchema,
	type JobWithRelations,
	type VideoStatusType,
} from "@repo/types";
import type { z } from "zod";
import { db } from "../client";
import { DBVideoService } from "./dbVideo.service";

export const DBJobService = {
	async createJob(data: z.infer<typeof JobUncheckedCreateInputSchema>): Promise<Job> {
		// creditUsageStatus is set by the database default (PENDING)
		// Set startTime if status is PROCESSING
		const jobData: any = { ...data };
		if (data.status === JobStatusSchema.Values.PROCESSING && !data.startTime) {
			jobData.startTime = new Date();
		}
		const result = await db.job.create({
			data: jobData
		});
		return JobSchema.parse(result);
	},

	async updateJobStatus(
		jobId: string,
		status: JobStatusType,
		errorMessage?: string[],
		creditUsageStatus?: CreditUsageStatusType,
	): Promise<Job> {
		const updateData: any = {
			status,
			...(creditUsageStatus ? { creditUsageStatus } : {}),
			...(errorMessage ? { errorMessage } : {}),
		};

		// Set endTime if status is COMPLETED or FAILED
		if (
			status === JobStatusSchema.Values.COMPLETED ||
			status === JobStatusSchema.Values.FAILED
		) {
			updateData.endTime = new Date();
		}

		const result = await db.job.update({
			where: { id: jobId },
			data: updateData,
		});
		return JobSchema.parse(result);
	},

	async updateJob(
		jobId: string,
		data: Partial<z.infer<typeof JobUncheckedCreateInputSchema>>,
	): Promise<Job> {
		const updateData: any = { ...data };

		// Set endTime if status is COMPLETED or FAILED
		if (
			data.status === JobStatusSchema.Values.COMPLETED ||
			data.status === JobStatusSchema.Values.FAILED
		) {
			updateData.endTime = new Date();
		}

		const result = await db.job.update({
			where: { id: jobId },
			data: updateData,
		});
		return JobSchema.parse(result);
	},

	async getJobsByOrganizationId(organizationId: string): Promise<Job[]> {
		const results = await db.job.findMany({ where: { organizationId } });
		return results.map((result) => JobSchema.parse(result));
	},

	async getJobById(jobId: string): Promise<JobWithRelations | null> {
		try {
			const job = await db.job.findFirst({
				where: {
					id: jobId,
					isDeleted: false,
				},
				include: {
					files: true,
					video: true,
					user: true,
					organization: true,
					key: true,
					purchase: true,
					renderHistories: true,
				},
			});
			return job as JobWithRelations;
		} catch (error: any) {
			// Handle Prisma MongoDB validation errors (enum mismatches, type conversions, etc.)
			// This can happen with old data that doesn't match current schema
			const errorMessage = error?.message || "";
			const isValidationError = 
				errorMessage.includes("Failed to convert") ||
				errorMessage.includes("not found in enum") ||
				errorMessage.includes("Invalid value for enum");

			if (isValidationError) {
				// Try fetching job with minimal relations first
				const job = await db.job.findFirst({
					where: {
						id: jobId,
						isDeleted: false,
					},
					include: {
						files: true,
						user: true,
						organization: true,
						key: true,
						purchase: true,
						// Exclude problematic relations
					},
				});

				if (!job) {
					return null;
				}

				// Initialize job with default values for potentially missing relations
				const jobWithRelations: any = {
					...job,
					video: null,
					renderHistories: [],
				};

				// Try to fetch video separately if it was causing issues
				if (errorMessage.includes("shorts")) {
					try {
						const video = await db.video.findUnique({
							where: { jobId: job.id },
						});
						
						if (video) {
							// Ensure shorts is always an array to prevent type errors
							const videoWithFixedShorts = {
								...video,
								shorts: Array.isArray(video.shorts) ? video.shorts : [],
							};
							jobWithRelations.video = videoWithFixedShorts;
						}
					} catch (videoError) {
						console.error(`Error fetching video for job ${jobId}:`, videoError);
						// Continue without video (already set to null)
					}
				} else {
					// Try to fetch video normally if it wasn't the problem
					try {
						const video = await db.video.findUnique({
							where: { jobId: job.id },
						});
						if (video) {
							const videoWithFixedShorts = {
								...video,
								shorts: Array.isArray(video.shorts) ? video.shorts : [],
							};
							jobWithRelations.video = videoWithFixedShorts;
						}
					} catch (videoError) {
						console.error(`Error fetching video for job ${jobId}:`, videoError);
					}
				}

				// Try to fetch renderHistories separately if it was causing issues
				if (errorMessage.includes("RenderHistoryStatus") || errorMessage.includes("renderHistories")) {
					try {
						const renderHistories = await db.renderHistory.findMany({
							where: { jobId: job.id },
							// Filter out invalid status values
							orderBy: { createdAt: "desc" },
						});
						
						// Filter out any renderHistories with invalid enum values
						const validRenderHistories = renderHistories.filter((rh: any) => {
							const validStatuses = ["QUEUED", "PROCESSING", "COMPLETED", "FAILED"];
							return validStatuses.includes(rh.status);
						});
						
						jobWithRelations.renderHistories = validRenderHistories;
					} catch (renderHistoryError) {
						console.error(`Error fetching renderHistories for job ${jobId}:`, renderHistoryError);
						// Continue without renderHistories (already set to empty array)
					}
				} else {
					// Try to include renderHistories if it wasn't the problem
					try {
						const renderHistories = await db.renderHistory.findMany({
							where: { jobId: job.id },
							orderBy: { createdAt: "desc" },
						});
						const validRenderHistories = renderHistories.filter((rh: any) => {
							const validStatuses = ["QUEUED", "PROCESSING", "COMPLETED", "FAILED"];
							return validStatuses.includes(rh.status);
						});
						jobWithRelations.renderHistories = validRenderHistories;
					} catch (renderHistoryError) {
						// If it fails, keep empty array (already set)
						console.error(`Error fetching renderHistories for job ${jobId}:`, renderHistoryError);
					}
				}

				return jobWithRelations as JobWithRelations;
			}
			throw error;
		}
	},
	async deleteJobById(jobId: string): Promise<void> {
		// Soft delete the job by setting isDeleted to true and archivedAt to current time
		await db.job.update({
			where: { id: jobId },
			data: { 
				isDeleted: true,
				archivedAt: new Date(),
			},
		});
	},
	async getVideosShortGenerationByOrganizationId(
		organizationId: string,
		startTime?: Date,
		endTime?: Date,
		purchaseId?: string,
	): Promise<Job[]> {
		const where: any = {
			organizationId,
			createdAt: {},
		};
		if (purchaseId) {
			where.purchaseId = purchaseId;
		}
		if (startTime) {
			where.createdAt.gte = startTime;
		}
		if (endTime) {			
			where.createdAt.lte = endTime;
		}
		const jobs = await db.job.findMany({
			where,
		});
		return jobs as Job[];
	},
	async manageExpiredVideoGenerations(organizationId: string): Promise<void> {
		const videos = await db.job.updateMany({
			where: {
				organizationId,
				status: {
					in: [JobStatusSchema.Values.PROCESSING],
				},
				createdAt: {
					lte: new Date(Date.now() - 2 * 60 * 60 * 1000),
				},
			},
			data: {
				status: JobStatusSchema.Values.FAILED,
			},
		});
	},
	async getVideosShortGenerationByOrganizationIdPaginated(
		organizationId: string,
		limit = 10,
		offset = 0,
		searchTerm?: string,
		status?: string,
	): Promise<{ data: Partial<JobWithRelations[]>; total: number }> {
		// Build the where clause
		const where: any = {
			organizationId,
			isDeleted: false,
			functionName: JobFunctionNameSchema.Values.VideoShorts,
		};

		// Add search term filter if provided
		if (searchTerm) {
			where.OR = [
				{ title: { contains: searchTerm, mode: "insensitive" } },
				{
					title: {
						contains: searchTerm,
						mode: "insensitive",
					},
				},
			];
		}

		// Add status filter if provided and not 'all'
		if (status && status !== "all") {
			where.status = status;
		}

		// Get total count with the same filters
		const total = await db.job.count({ where });

		// Get paginated results with optimized video include
		const jobs: Partial<any[]> = await db.job.findMany({
			where,
			include: {
				video: {
					select: {
						id: true,
						jobId: true,
						sourceId: true,
						videoLength: true,
						title: true,
						choosedDefinition: true,
						status: true,
						createdAt: true,
						files: true,
						shorts: {
							select: {
								id: true,
								status: true,
							},
						},
					},
				},
			},
			orderBy: {
				createdAt: "desc",
			},
			skip: offset,
			take: limit,
		});

		return {
			data: jobs,
			total,
		};
	},
	async changeVideoJobStatus(
		jobId: string,
		jobStatus: JobStatusType,
		videoStatus: VideoStatusType,
	) {
		const job = await db.job.findFirst({
			where: { id: jobId },
			include: { video: true },
		});
		if (job?.video?.id) {
			await DBVideoService.changeVideoStatusAndSave(
				job?.video?.id,
				videoStatus,
			);
		}
		await db.job.update({
			where: {
				id: jobId,
			},
			data: {
				status: jobStatus,
			},
		});
	},
};
